/* ROI Calculator for Pricing page — inputs orders/month, marketplaces, warehouses,
 * ops employees; recommends a Jini plan and estimates savings/hours/ROI.
 */
import { useMemo, useState } from "react";
import { Calculator, TrendingUp, Clock, IndianRupee, Package } from "lucide-react";
import {
  RED, INK, MUTED, CREAM, BORDER, PrimaryButton, FieldLabel,
} from "@/components/marketing/atoms";

/* ---- plan definitions (exactly per brochure) ---- */
export const PLANS = [
  { key: "launch",     name: "Jini Launch",     price: 2500,  validityMonths: 1,  costPerOrder: 1.5,  credits: 1667,  ideal: "Startups" },
  { key: "growth",     name: "Jini Growth",     price: 5000,  validityMonths: 3,  costPerOrder: 1.2,  credits: 4167,  ideal: "Growing Brands" },
  { key: "scale",      name: "Jini Scale",      price: 7500,  validityMonths: 6,  costPerOrder: 0.9,  credits: 8333,  ideal: "Scaling Sellers", popular: true },
  { key: "enterprise", name: "Jini Enterprise", price: 10000, validityMonths: 12, costPerOrder: 0.9,  credits: 11111, ideal: "Large Brands & Agencies" },
];

/* Pick the smallest plan whose credit pool covers monthly orders x validity.
 * If none fits, recommend Enterprise. */
function recommendPlan(ordersPerMonth) {
  for (const p of PLANS) {
    const capacityOverValidity = p.credits; // credits included over the plan validity
    const ordersOverValidity = ordersPerMonth * p.validityMonths;
    if (ordersOverValidity <= capacityOverValidity) return p;
  }
  return PLANS[PLANS.length - 1];
}

function toINR(n) {
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

export default function ROICalculator() {
  const [orders, setOrders] = useState(2000);
  const [marketplaces, setMarketplaces] = useState(3);
  const [warehouses, setWarehouses] = useState(1);
  const [employees, setEmployees] = useState(3);

  const result = useMemo(() => {
    const plan = recommendPlan(orders);
    const creditsNeededPerMonth = Math.max(orders, 0);

    // Baseline manual cost: ~4 min/order + reconciliation overhead + tool overhead per marketplace/warehouse
    const minsPerOrderManual = 4;
    const hoursSavedPerMonth =
      (orders * minsPerOrderManual) / 60 * 0.75 // AI cuts ~75% of order-touch time
      + marketplaces * 8
      + warehouses * 6;

    // Assume ₹300/hr fully-loaded operations cost in India (typical ops exec)
    const rupeesSavedPerMonth = hoursSavedPerMonth * 300;

    // Monthly cost of the recommended plan (amortized)
    const monthlyPlanCost = plan.price / plan.validityMonths;
    const monthlyNet = rupeesSavedPerMonth - monthlyPlanCost;
    const roiPct = monthlyPlanCost > 0 ? (monthlyNet / monthlyPlanCost) * 100 : 0;

    // Extra credits needed (over plan capacity per month, if any)
    const monthlyCapacity = plan.credits / plan.validityMonths;
    const extraCreditsNeeded = Math.max(0, creditsNeededPerMonth - monthlyCapacity);

    return {
      plan,
      creditsNeededPerMonth,
      hoursSavedPerMonth,
      rupeesSavedPerMonth,
      monthlyPlanCost,
      monthlyNet,
      roiPct,
      extraCreditsNeeded,
    };
  }, [orders, marketplaces, warehouses, employees]);

  return (
    <div
      data-testid="roi-calculator"
      style={{
        background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16,
        padding: 28, boxShadow: "0 24px 60px -30px rgba(15,23,42,0.15)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div
          style={{
            width: 44, height: 44, borderRadius: 10, background: "#FFF5F5",
            color: RED, display: "grid", placeItems: "center",
          }}
        >
          <Calculator size={22} />
        </div>
        <div>
          <div style={{ fontWeight: 800, fontSize: 20, color: INK }}>ROI Calculator</div>
          <div style={{ fontSize: 13, color: MUTED }}>See your monthly savings & recommended plan in seconds.</div>
        </div>
      </div>

      <div
        className="roi-grid"
        style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 28 }}
      >
        {/* ------------ Inputs ------------ */}
        <div>
          <NumInput
            label="Orders per month"
            value={orders}
            onChange={setOrders}
            min={0}
            step={100}
            testid="roi-orders"
          />
          <div style={{ height: 14 }} />
          <NumInput
            label="Marketplaces connected"
            value={marketplaces}
            onChange={setMarketplaces}
            min={0}
            max={40}
            step={1}
            testid="roi-marketplaces"
          />
          <div style={{ height: 14 }} />
          <NumInput
            label="Warehouses"
            value={warehouses}
            onChange={setWarehouses}
            min={0}
            max={50}
            step={1}
            testid="roi-warehouses"
          />
          <div style={{ height: 14 }} />
          <NumInput
            label="Employees managing operations"
            value={employees}
            onChange={setEmployees}
            min={0}
            max={200}
            step={1}
            testid="roi-employees"
          />

          <div style={{ marginTop: 22 }}>
            <PrimaryButton as="link" to="/contact-sales" testid="roi-talk-to-sales">
              Talk to Sales
            </PrimaryButton>
          </div>
        </div>

        {/* ------------ Result ------------ */}
        <div
          style={{
            background: CREAM, borderRadius: 12, padding: 22,
            border: `1px solid ${BORDER}`,
          }}
          data-testid="roi-result"
        >
          <div
            style={{
              display: "inline-block", background: "#fff", padding: "6px 12px",
              borderRadius: 999, border: `1px solid ${RED}33`,
              color: RED, fontWeight: 700, fontSize: 11, letterSpacing: "0.14em",
              textTransform: "uppercase",
            }}
          >
            Recommended
          </div>
          <div style={{ marginTop: 12, fontSize: 26, fontWeight: 800, color: INK, letterSpacing: "-0.02em" }}
               data-testid="roi-plan-name">
            {result.plan.name}
          </div>
          <div style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>
            {toINR(result.plan.price)} · {result.plan.validityMonths} month{result.plan.validityMonths > 1 ? "s" : ""} validity · {result.plan.credits.toLocaleString("en-IN")} credits
          </div>

          <div style={{ height: 16, borderTop: `1px dashed ${BORDER}`, margin: "18px 0 8px" }} />

          <StatRow
            icon={<Package size={16} color={RED} />}
            label="Credits required / month"
            value={result.creditsNeededPerMonth.toLocaleString("en-IN")}
            testid="roi-credits"
          />
          <StatRow
            icon={<Clock size={16} color={RED} />}
            label="Hours saved / month"
            value={`${Math.round(result.hoursSavedPerMonth)} hrs`}
            testid="roi-hours"
          />
          <StatRow
            icon={<IndianRupee size={16} color={RED} />}
            label="Estimated savings / month"
            value={toINR(result.rupeesSavedPerMonth)}
            testid="roi-savings"
          />
          <StatRow
            icon={<TrendingUp size={16} color={RED} />}
            label="Monthly ROI"
            value={`${Math.round(result.roiPct)}%`}
            testid="roi-percent"
            highlight
          />

          {result.extraCreditsNeeded > 0 && (
            <div
              style={{
                marginTop: 12, padding: "10px 12px", background: "#FFF7ED",
                border: "1px solid #FED7AA", color: "#9A3412",
                borderRadius: 8, fontSize: 12, lineHeight: 1.5,
              }}
              data-testid="roi-topup-hint"
            >
              You may need <b>{Math.round(result.extraCreditsNeeded).toLocaleString("en-IN")}</b> extra credits/month.
              Grab an <b>Add-on Credit Pack</b> below or talk to sales for volume discounts.
            </div>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) { .roi-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}

function NumInput({ label, value, onChange, testid, ...rest }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        data-testid={testid}
        style={{
          width: "100%", padding: "12px 14px",
          border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 15,
          fontWeight: 600, color: INK, outline: "none",
        }}
        {...rest}
      />
    </div>
  );
}

function StatRow({ icon, label, value, testid, highlight }) {
  return (
    <div
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "8px 0",
      }}
      data-testid={testid}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {icon}
        <span style={{ fontSize: 13, color: MUTED }}>{label}</span>
      </div>
      <div
        style={{
          fontWeight: 800, fontSize: highlight ? 20 : 16,
          color: highlight ? RED : INK,
        }}
      >
        {value}
      </div>
    </div>
  );
}
